import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useStore } from 'src/contexts/StoreContext';
import { EDITOR_BASE_URL } from 'src/app/settings';
import { ROUTE_PATHS } from 'src/routes/_shared/routes.constants';
import Spinner from '@photosi/rn-components/spinner/Spinner';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { icon, neutrals, text } from 'src/theme/colors';
import Clipboard from '@react-native-clipboard/clipboard';
import { stylesDesktop, stylesMobile } from './PickerFamilyFriends.style';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import amplitude from 'src/_shared/amplitude';
import { TextIconButton } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { SourcesEventType } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { LinearGradient } from 'expo-linear-gradient';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { ShareButton } from '@photosi/rn-components/buttons/share-button/ShareButton';
import { PickerProps } from '../Picker.types';

export const PickerFamilyFriends = observer(
  ({ onConfirmStep, isOnline, focusOrder, focusGroup, focusId }: PickerProps) => {
    const {
      i18n,
      project: { createShareTokenFamilyAndFriends, shareTokenFamilyAndFriends, setFromSmartphoneOrFamilyAndFriends },
      data: { percentage },
    } = useStore();
    const { isDesktop } = useResponsiveScreen();
    const [url, setUrl] = useState<string>('');
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const styles = isDesktop ? stylesDesktop : stylesMobile;

    useEffect(() => {
      setFromSmartphoneOrFamilyAndFriends(true);
      if (isOnline) {
        if (!shareTokenFamilyAndFriends) {
          createShareTokenFamilyAndFriends().then(res => {
            generateUrl(res);
          });
        } else {
          generateUrl(shareTokenFamilyAndFriends);
        }
      }
    }, [isOnline]);

    useEffect(() => {
      if (percentage > 0) onConfirmStep();
    }, [percentage]);

    const generateUrl = (token: string) => {
      const urlUploadFamilyAndFriends = `${EDITOR_BASE_URL}/${ROUTE_PATHS.UPLOAD_FAMILY_FRIENDS}?shareToken=${token}`;
      setUrl(urlUploadFamilyAndFriends);
    };
    const copyUrl = async () => {
      Clipboard.setString(`${url}&origin=copy_link`);
      setShowTooltip(true);
      setTimeout(() => {
        setShowTooltip(false);
      }, 2000);
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.shareLink, {
        type: 'copy_link',
        share_token_id: shareTokenFamilyAndFriends,
        source_type: SourcesEventType.FamilyFriends,
      });
    };

    return (
      <View style={styles.details}>
        <View style={styles.titleWrapper}>
          <TextLato style={styles.mainTitle}>{i18n.t('upload:upload-family-title')}</TextLato>
          {url.length ? (
            <TextLato style={styles.description}>{i18n.t('upload:upload-family-description')}</TextLato>
          ) : (
            <TextLato style={styles.description}>{i18n.t('upload:upload-family-loading-description')}</TextLato>
          )}
        </View>
        <View style={styles.wrapper}>
          {url.length ? (
            <View style={styles.share}>
              <View style={styles.linkWrapper}>
                <TextLato style={styles.ctaUrlShare} numberOfLines={1} selectable={false}>
                  {url}
                </TextLato>
                <View>
                  <LinearGradient
                    colors={['transparent', neutrals.white]}
                    style={styles.gradient}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    pointerEvents="none"
                  />
                  {showTooltip && (
                    <View style={styles.tooltip}>
                      <View style={styles.tooltipIcon}>
                        <Icon name={ICON_NAMES.Check} filled color={icon.iconSuccess} />
                      </View>
                      <TextLato style={styles.tooltipText}>{i18n.t('modal-picker-smartphone:copied')}</TextLato>
                    </View>
                  )}
                  <TextIconButton
                    onPress={copyUrl}
                    focusId={`${focusId}.picker-family-friends-step-button-copy`}
                    focusOrder={focusOrder + 1}
                    focusGroup={focusGroup}
                    text={i18n.t('modal-picker-family-friends:copy-cta')}
                    textStyle={styles.buttonText}
                    style={{ minHeight: 32, minWidth: 118, justifyContent: 'center' }}
                    innerStyle={{
                      flexDirection: 'row',
                      justifyContent: 'center',
                      gap: 8,
                      alignItems: 'center',
                    }}
                    iconName={ICON_NAMES.Copy}
                    iconStyle={styles.iconCopy}
                    size={TEXT_ICON_BUTTON_SIZES.SMALL}
                    type={TEXT_ICON_BUTTON_TYPES.ICON_LEFT}
                  />
                </View>
              </View>
              <ShareButton
                focusId={`${focusId}.picker-family-friends-step-button-share`}
                focusGroup={focusGroup}
                focusOrder={focusOrder + 2}
                cta={i18n.t('modal-picker-family-friends:share-cta')}
                message={i18n.t('modal-picker-family-friends:share-message')}
                url={url}
                shareTokenFamilyAndFriends={shareTokenFamilyAndFriends}
              />
            </View>
          ) : (
            <Spinner />
          )}
        </View>
      </View>
    );
  },
);
